/**
 * Choosing what goes into the loop.
 *
 * A grid of thumbnails rather than a list of file names, because nobody
 * remembers which of `promo-final-v3.mp4` and `promo-final-v4.mp4` is the one
 * with the right price on it. Several can be picked at once and they are added in
 * the order they were picked, which is almost always the order somebody wants
 * them in.
 *
 * Every tile is a real button, so the whole picker is reachable by tab and space
 * with no extra machinery.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Modal } from '../Modal.js';
import { ErrorBlock, InlineEmpty, LoadingBlock } from '../Feedback.js';
import { formatBytes } from '../../lib/format.js';
import type { MediaAsset } from '../../lib/media.js';
import { formatRuntime, isVideoMediaType } from './runtime.js';

export interface MediaPickerProps {
  open: boolean;
  assets: readonly MediaAsset[];
  loading: boolean;
  error: unknown;
  onAdd: (assets: readonly MediaAsset[]) => void;
  onClose: () => void;
}

export function MediaPicker({
  open,
  assets,
  loading,
  error,
  onAdd,
  onClose,
}: MediaPickerProps): ReactElement {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<readonly string[]>([]);

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return assets;
    return assets.filter((asset) => asset.title.toLowerCase().includes(needle));
  }, [assets, search]);

  const close = (): void => {
    setPicked([]);
    setSearch('');
    onClose();
  };

  const toggle = (assetId: string): void => {
    setPicked((current) =>
      current.includes(assetId)
        ? current.filter((id) => id !== assetId)
        : [...current, assetId],
    );
  };

  const add = (): void => {
    const byId = new Map(assets.map((asset) => [asset.assetId, asset]));
    // Added in the order they were clicked, not the order the library lists them.
    const chosen = picked
      .map((assetId) => byId.get(assetId))
      .filter((asset): asset is MediaAsset => asset !== undefined);
    if (chosen.length > 0) onAdd(chosen);
    close();
  };

  return (
    <Modal
      open={open}
      size="wide"
      title="Add media"
      description="Everything this playlist can reach: its own store's library plus anything shared across stores."
      onClose={close}
      footer={
        <>
          <button type="button" className="button button--ghost" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={add}
            disabled={picked.length === 0}
          >
            {picked.length === 0
              ? 'Add to playlist'
              : `Add ${picked.length === 1 ? '1 item' : `${picked.length} items`}`}
          </button>
        </>
      }
    >
      <label className="field">
        <span className="field__label">Search by name</span>
        <input
          type="search"
          className="input"
          value={search}
          placeholder="Two for Tuesday"
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
      </label>

      {loading ? <LoadingBlock label="Loading the media library…" /> : null}

      {error !== null && error !== undefined ? (
        <ErrorBlock error={error} action="load the media library" />
      ) : null}

      {!loading && assets.length === 0 ? (
        <InlineEmpty
          title="Nothing in the library yet"
          description="Upload a video or an image on the Media page first. A playlist can only point at media that already exists, because the TVs download files by id."
        />
      ) : null}

      {!loading && assets.length > 0 && matches.length === 0 ? (
        <InlineEmpty
          title="Nothing matches that"
          description="No asset in this playlist's scope has a name containing what you typed."
        />
      ) : null}

      {matches.length > 0 ? (
        <ul className="picker-grid">
          {matches.map((asset) => (
            <li key={asset.assetId}>
              <button
                type="button"
                className={
                  picked.includes(asset.assetId) ? 'picker-tile is-picked' : 'picker-tile'
                }
                aria-pressed={picked.includes(asset.assetId)}
                onClick={() => toggle(asset.assetId)}
              >
                <span className="picker-tile__frame">
                  {asset.thumbUrl === null ? (
                    <span className="picker-tile__placeholder" aria-hidden="true">
                      {isVideoMediaType(asset.type) ? 'MP4' : 'IMG'}
                    </span>
                  ) : (
                    <img src={asset.thumbUrl} alt="" draggable={false} />
                  )}
                </span>
                <span className="picker-tile__title">{asset.title || asset.assetId}</span>
                <span className="picker-tile__meta">{describeAsset(asset)}</span>
                {asset.storeId === null ? (
                  <span className="chip chip--neutral picker-tile__chip">Shared</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </Modal>
  );
}

function describeAsset(asset: MediaAsset): string {
  const shape = `${asset.width} × ${asset.height}`;
  const size = formatBytes(asset.bytes);
  if (!isVideoMediaType(asset.type)) return `${shape} · ${size}`;
  const length = asset.durationMs === null ? 'length unknown' : formatRuntime(asset.durationMs);
  return `${shape} · ${length} · ${size}`;
}
